import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../api/axios';
import ImageUploader from './ImageUploader';

const categories = [
  'Нужна помощь специалиста',
  'Хочу лучше понимать ребёнка',
  'Мир творчества',
  'Спорт и движение',
  'Общение и развитие',
  'Особые образовательные потребности и поддержка семьи',
  'Другое',
];

const defaultFormData = {
  title: '',
  description: '',
  url: '',
  category: 'Другое',
  imageUrl: '',
  order: 0,
  isActive: true,
};

const UsefulResourcesManager = () => {
  const [resources, setResources] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get('/useful-resources');
      setResources(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке ресурсов:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setFormData(defaultFormData);
  };

  const handleEdit = (resource) => {
    setEditingId(resource._id);
    setFormData({
      title: resource.title || '',
      description: resource.description || '',
      url: resource.url || '',
      category: resource.category || 'Другое',
      imageUrl: resource.imageUrl || '',
      order: resource.order || 0,
      isActive: resource.isActive !== false,
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот ресурс?')) {
      return;
    }

    try {
      await axios.delete(`/useful-resources/${id}`);
      fetchResources();
    } catch (error) {
      console.error('Ошибка при удалении ресурса:', error);
      alert(error.response?.data?.message || 'Не удалось удалить ресурс');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Заполните название');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/useful-resources/${editingId}`, formData);
      } else {
        await axios.post('/useful-resources', formData);
      }
      handleClose();
      fetchResources();
    } catch (error) {
      console.error('Ошибка при сохранении ресурса:', error);
      alert(error.response?.data?.message || 'Не удалось сохранить ресурс');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Навигатор для родителей
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Добавить организацию/ресурс
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Ссылка</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell>Активен</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map((resource) => (
              <TableRow key={resource._id}>
                <TableCell>{resource.title}</TableCell>
                <TableCell>{resource.category}</TableCell>
                <TableCell>
                  {resource.url ? (
                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                      {resource.url}
                    </a>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{resource.order}</TableCell>
                <TableCell>{resource.isActive ? 'Да' : 'Нет'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(resource)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(resource._id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Редактировать ресурс' : 'Добавить ресурс'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Название"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Описание (контакты, адрес, что делает организация)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
          <TextField
            label="Ссылка (необязательно)"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Категория</InputLabel>
            <Select
              value={formData.category}
              label="Категория"
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Изображение (опционально)
            </Typography>
            <ImageUploader
              currentImage={formData.imageUrl}
              onUploadSuccess={(url) => setFormData({ ...formData, imageUrl: url })}
            />
          </Box>
          <TextField
            label="Порядок"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Активен"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Обновить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsefulResourcesManager;
